import React, {
	Dispatch,
	SetStateAction,
	useEffect,
	useRef,
	useState,
} from 'react';
import {
	Box,
	Button,
	TextField,
	IconButton,
	Stack,
	InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Search } from '@mui/icons-material';
import AddQuotationModal from '../modalContent/AddQuotationModal';
import AddQuotationModalV3 from '../modalContent/AddQuotationModalV3';
import { TbArrowRight } from 'react-icons/tb';
import { useGlobalState } from 'src/stateProviders/GlobalStateProvider';
import { useApi } from 'src/hooks/useApi';
import AppAutocomplete from 'src/globalComponents/AppAutoComplete';

const GridToolbar: React.FC<{
	creator: string;
	setCreator: Dispatch<SetStateAction<string>>;
	status: string;
	setStatus: Dispatch<SetStateAction<string>>;
	filter: string;
	setFilter: Dispatch<SetStateAction<string>>;
	handleRefresh?: () => void;
	allData?: any[]; // Add allData prop to extract unique values
	setCardFilter?: Dispatch<SetStateAction<string>>;
	setLast30Days?: Dispatch<SetStateAction<boolean>>;
	setSelectedCardStatus?: Dispatch<SetStateAction<string>>;
	hybridMode: boolean;
}> = ({
	creator,
	setCreator,
	status,
	setStatus,
	filter,
	setFilter,
	handleRefresh,
	allData = [],
	setCardFilter,
	setLast30Days,
	setSelectedCardStatus,
	hybridMode,
}) => {
	const [localSearchInput, setLocalSearchInput] = React.useState(filter);
	const debounceTimer = useRef<NodeJS.Timeout>();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isFocused, setIsFocused] = React.useState(false);
	const [cursorPosition, setCursorPosition] = React.useState(0);
	const globalState = useGlobalState();
	const api = useApi();
	const [filters, setFilters] = useState<{
		creators: any[];
		statuses: any[];
	}>({ creators: [], statuses: [] });

	// Extract unique creators from actual data
	const creators = React.useMemo(() => {
		if (hybridMode) {
			if (!allData || allData.length === 0) return [];

			const uniqueCreators = [
				...new Set(
					allData
						.map((row) => row.firstNameLastName)
						.filter((name) => name && name.trim())
				),
			].sort((a, b) => a.localeCompare(b));

			return [
				{ value: '', label: 'All' },
				...uniqueCreators.map((name) => ({ value: name, label: name })),
			];
		} else {
			return [
				{ value: '', label: 'All' },
				...(filters?.creators
					? filters.creators
							.map((creator) => {
								const name = `${creator.firstName} ${creator.lastName}`;

								return {
									value: name,
									label: name,
								};
							})
							.filter((creator) => creator.label && creator.label.trim().length)
							.sort((a, b) => a.label.localeCompare(b.label))
					: []),
			];
		}
	}, [allData, hybridMode, filters]);

	useEffect(() => {
		(async () => {
			if (!hybridMode) {
				const filtersResult = await api.quotations.getQuotationFilters();
				setFilters(filtersResult);
			}
		})();
	}, [hybridMode]);

	useEffect(() => {
		setLocalSearchInput(filter);
	}, [filter]);

	useEffect(() => {
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current);
		}

		debounceTimer.current = setTimeout(() => {
			if (localSearchInput !== filter) {
				setFilter(localSearchInput);
			}
		}, 1);

		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}
		};
	}, [localSearchInput, filter, setFilter]);

	useEffect(() => {
		if (
			isFocused &&
			inputRef.current &&
			document.activeElement !== inputRef.current
		) {
			inputRef.current.focus();
			inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
		}
	});

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
			setIsFocused(true);
		}
	}, []);

	const statuses = React.useMemo(() => {
		if (hybridMode) {
			if (!allData || allData.length === 0) return [];

			const uniqueStatuses = [
				...new Set(
					allData
						.map((row) => row.status)
						.filter((status) => status && status.trim())
				),
			].sort((a, b) => a.localeCompare(b));

			return [
				{ value: '', label: 'All' },
				...uniqueStatuses.map((status) => ({
					value: status,
					label: status.charAt(0).toUpperCase() + status.slice(1),
				})),
			];
		} else {
			return [
				{ value: '', label: 'All' },
				...filters.statuses
					.map((status) => status.status)
					.filter((status) => status && status.trim().length)
					.sort((a, b) => a.localeCompare(b))
					.map((status) => {
						return {
							value: status,
							label: status.charAt(0).toUpperCase() + status.slice(1),
						};
					}),
			];
		}
	}, [allData, hybridMode, filters]);

	const [addModalOpen, setAddModalOpen] = React.useState(false);

	return globalState.company ? (
		<Box sx={{ paddingTop: 1, paddingBottom: 2 }}>
			<Stack direction="row" spacing={2} alignItems="center">
				<TextField
					inputRef={inputRef}
					sx={{
						width: {
							xs: '100px',
							sm: '180px',
							md: '220px',
							lg: '260px',
						},
					}}
					aria-label="Search"
					placeholder="Search quotes"
					size="small"
					variant="outlined"
					value={localSearchInput}
					onChange={(e) => {
						setLocalSearchInput(e.target.value);
						setCursorPosition(e.target.selectionStart || 0);
					}}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					onSelect={(e) =>
						setCursorPosition(e.currentTarget.selectionStart || 0)
					}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<Search fontSize="small" />
								</InputAdornment>
							),
						},
					}}
				/>
				<AppAutocomplete
					options={creators}
					getOptionLabel={(option) => option.label}
					value={creators.find((user) => user.value === creator) || null}
					onChange={(_, newValue) => {
						setCreator(newValue?.value || ''); // Empty string means "All"
					}}
					style={{ marginLeft: 'auto' }}
					sx={{
						width: {
							xs: '100px',
							sm: '160px',
							md: '200px',
							lg: '240px',
						},
					}}
					renderInput={(params) => (
						<TextField {...params} label="Created By" size="small" />
					)}
					isOptionEqualToValue={(option, value) => option.value === value.value}
				/>
				<AppAutocomplete
					options={statuses}
					getOptionLabel={(option) => option.label}
					value={statuses.find((s) => s.value === status) || null}
					onChange={(_, newValue) => {
						setStatus(newValue?.value || ''); // Empty string means "All"
						// Clear card-specific filters when using toolbar status dropdown
						if (setCardFilter) {
							setCardFilter('');
						}
						if (setLast30Days) {
							setLast30Days(false);
						}
						if (setSelectedCardStatus) {
							setSelectedCardStatus('');
						}
					}}
					sx={{
						width: {
							xs: '100px',
							sm: '140px',
							md: '180px',
							lg: '220px',
						},
					}}
					renderInput={(params) => (
						<TextField {...params} label="Status" size="small" />
					)}
					isOptionEqualToValue={(option, value) => option.value === value.value}
				/>
				<IconButton
					color="primary"
					onClick={handleRefresh}
					sx={{
						border: '1px dotted',
						borderColor: 'primary.main',
					}}
				>
					<RefreshIcon />
				</IconButton>
				<Button
					variant="contained"
					color="primary"
					endIcon={<TbArrowRight />}
					onClick={() => setAddModalOpen(true)}
					sx={{
						minWidth: {
							xs: '90px',
							sm: '120px',
							md: '140px',
						},
					}}
				>
					Add Quote
				</Button>
				{addModalOpen &&
					(globalState.company?.betaCanvasEnabled ? (
						<AddQuotationModalV3
							modalOpen={addModalOpen}
							setModalOpen={setAddModalOpen}
						/>
					) : (
						<AddQuotationModal
							modalOpen={addModalOpen}
							setModalOpen={setAddModalOpen}
						/>
					))}
			</Stack>
		</Box>
	) : (
		<></>
	);
};

export default React.memo(GridToolbar);
